<template>
    <div class="container" v-if="!displayModalChoixSite">
        <div class="title-container">
            <span>
                {{ $t('rapports.intervention.title') }}
                <ReglePastille cle="rapportsIntervention.definition" />
            </span>
        </div>
        <div class="content-container">
            <div class="filter-container">
                <div class="filter-site-container" v-if="!isUniqueSite">
                    {{$t('intervention.non-preventive.filter')}}
                    <ButtonBlue
                        :on-click="selectionnerSite"
                        :text="$t('bouton.choix.site')"
                        class="button-confirm btn"
                        :disabled="tableData.length === 0 && isLoading || filtreEquipementLoading || filtreGroupeLoading"
                    />
                </div>
                <div class="radio-container">
                  <RadioBox :selected="isPreventif"/>
                  <span class="span-container">{{$t('document.rapports.intervention.preventive.radio')}}</span>
                </div>
                <Filter
                    @search="doFilter"
                    @setDocumentDateFrom="this.setDocumentDateFrom"
                    @setDocumentDateTo="this.setDocumentDateTo"
                    @addFilterGroupe="this.addFilterGroupe"
                    @addFilterSousGroupe="this.addFilterSousGroupe"
                    @removeFilterGroupe="this.removeFilterGroupe"
                    @removeFilterSousGroupe="this.removeFilterSousGroupe"
                    @addFilterEquipement="this.addFilterEquipement"
                    @removeFilterEquipement="this.removeFilterEquipement"
                    @clearFilter="this.clearFilter"
                    @groupeToggleSelectAll="this.groupeToggleSelectAll"
                    @equipementToggleSelectAll="this.equipementToggleSelectAll"
                    @sousGroupeToggleSelectAll="this.sousGroupeToggleSelectAll"
                    :sousGroupeList="this.sousGroupeList"
                    :filter="this.filter"
                    :groupeList="this.groupeList"
                    :equipementList="this.equipementListFiltered"
                    :searchSite="searchSite"
                    class="filter"
                />
            </div>
            <div class="dataTable">
                <div>
                    <h2>
                        {{ $tc('rapports.intervention.dataTable.title', +count) }}
                        <ReglePastille cle="rapportsIntervention.portee" />
                        <ReglePastille cle="rapportsIntervention.filtresEnCascade" />
                    </h2>
                    <div>
                        <BorderedIconButton
                            @click="getDocuments"
                            class="button-left" v-if="!isDownloadDisabled"
                            :imgUrl="iconeDownload"
                            :title="$t('list.download.selection')"/>
                        <ReglePastille cle="rapportsIntervention.telechargementGroupe" />
                        <BorderedIconButton
                            @click="downloadAllDocuments"
                            v-if='isAtLeastOneReport'
                            :imgUrl="iconeDownload"
                            :title="$t('list.download.all')"
                            class="button-right"/>
                        <ReglePastille cle="rapportsIntervention.telechargerTout" />
                    </div>
                </div>
                <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
                <div v-else ref="content" @scroll="handleScroll" class="datatable-container-smaller">
                    <DataTable
                        :tableData="this.tableData"
                        :isLoading="isLoading"
                        :tableHeader="this.tableHeader"
                        :noDataText="$t('search.notFound')"
                        :documentListToDownload="documentListToDownload"
                        @sort="doSort"
                        @select="doSelect"
                    />
                </div>
            </div>
        </div>
    </div>
    <ModalChoixSite v-else
                    @cancel="modalChoixSiteCancel"
                    @valider="validerSite"
                    :selected-site="null"
                    :selected-sites="filter.siteList"
                    :is-multi-site="true"
                    :inclure-termines="true"/>
    <WaitingModal v-if="isZipLoading"/>
    <ModalDownloadDocuments v-if="showModalDownloadAll"
                            @cancel="cancelModalDownloadAll"
                            @downloadDocuments="setFilterForDownloadAllDocuments"
                            :nb-documents-modal="getNbRapports"/>
    <ModalChoixNomZip v-if='showModalNomZip'
                      @cancel="showModalNomZip = false"
                      @download="downloadDocuments"/>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import DataTable from '@/components/ui-components/DataTable.vue';
import Filter from '@/modules/Common/components/Filter.vue';
import { TableHeader } from '@/models/TableHeader';
import { SortType, sortValue } from '@/models/SortType';
import SiteListService, { SiteSearchDTO } from '@/modules/SiteList/service/SiteListService';
import { RAPPORT } from '@/modules/Rapport/store/mutation-types';
import { Site } from '@/models/Site';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import { FilterRapport, RapportToDownload } from '@/modules/Rapport/services/RapportService';
import ModalChoixSite from '@/modules/Common/components/ModalChoixSite.vue';
import { store } from '@/plugins/store';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import { RapportActions } from '@/modules/Rapport/store/action-types';
import { DocumentReglementaire } from '@/models/DocumentReglementaire';
import RadioBox from '@/components/ui-components/RadioBox.vue';
import ModalChoixNomZip from '@/components/ui-components/ModalChoixNomZip.vue';
import ModalDownloadDocuments from '@/components/ui-components/ModalDownloadDocuments.vue';
import { EquipementFilter } from '@/models/EquipementFilter';
import EquipementService from '@/modules/Equipements/services/EquipementService';
import { Groupe } from '@/models/Groupe';
import { SousGroupe } from '@/models/SousGroupe';
import { DOCUMENT_REGLEMENTAIRE } from '@/modules/DocumentReglementaire/store/mutation-types';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Data {
    siteList: any[];
    filter: {
        siteList: any[];
        groupeList: any[];
        sousGroupeList: any[];
        equipementList: any[];
        documentDateFrom: string;
        documentDateTo: string;
    };
    siteListTotal: any[];
    groupeList: Groupe[];
    sousGroupeList: SousGroupe[];
    equipementList: EquipementFilter[];
    equipementListFiltered: EquipementFilter[];
    debounce: number;
    searchSite: string;
    documentListToDownload: string[];
    displayModalChoixSite: boolean;
    isUniqueSite: boolean;
    showModalDownloadAll: boolean;
    isPreventif: boolean;
    showModalNomZip: boolean;
    rapportsInterventionToDownload: RapportToDownload;
    filtreRapportIntervention: FilterRapport;
    isDownloadAll: boolean;
    filtreGroupeLoading: boolean;
    filtreEquipementLoading: boolean;
}

export default defineComponent({
    name: 'RapportPage',
    components: {
        ModalDownloadDocuments,
        ModalChoixNomZip,
        RadioBox,
        Filter,
        DataTable,
        LoaderComponent,
        BorderedIconButton,
        ModalChoixSite,
        ButtonBlue,
        WaitingModal,
        ReglePastille,
    },
    data(): Data{
        return {
            siteList: [],
            filter: {
                siteList: [],
                groupeList: [],
                sousGroupeList: [],
                equipementList: [],
                documentDateFrom: '',
                documentDateTo: '',
            },
            siteListTotal: [],
            groupeList: [],
            sousGroupeList: [],
            equipementList: [],
            equipementListFiltered: [],
            debounce: 0,
            searchSite: '',
            documentListToDownload: [],
            displayModalChoixSite: false,
            isUniqueSite: false,
            showModalDownloadAll: false,
            isPreventif: true,
            showModalNomZip: false,
            rapportsInterventionToDownload: {
              interventionIds: [],
              chantierIds: [],
              nomZip: undefined
            },
            filtreRapportIntervention: {isPreventif: true},
            isDownloadAll: false,
            filtreGroupeLoading: false,
            filtreEquipementLoading: false
        };
    },
    beforeMount() {
        this.filter.siteList = this.filterSite;
        SiteListService.getSiteList(new SiteSearchDTO(0, '', true, true)).then(async res => {
            if (res.data.length === 1) {
                this.isUniqueSite = true;
                this.displayModalChoixSite = false;
            }
            this.siteList = res.data.map(
                ({id, chantierId, description}: { id: string; chantierId: number; description: string }) => ({
                    id,
                    chantierId,
                    description,
                })
            );

            this.siteListTotal = this.siteList;
            store.commit(`rapportState/${RAPPORT.SET_FILTER_SITE}`, this.siteList);
            this.getListGroupBySite();
            this.getEquipementList();
        });

        this.filter.documentDateFrom = this.filterDocumentDateFrom;
        this.filter.documentDateTo = this.filterDocumentDateTo;

        if (!this.displayModalChoixSite) {
            this.getRapportData();
        }
    },
    setup() {
        const content = ref<Element>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    mounted() {
        this.content?.scrollTo({ top: this.scroll });
    },
    methods: {
        getRapportData(page = 0) {
            page === 0 && this.content?.scrollTo({ top: 0 });
            this.$store.dispatch(`rapportState/${RapportActions.GET_RAPPORT}`, {
                page,
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                adresseProjetId: this.filterSite?.length === this.siteList?.length ? [] : [...new Set(this.filterSite?.map(x => x.id))],
                chantierIds:  this.filterSite?.length === this.siteList?.length ? [] : [...new Set(this.filterSite?.map(x => x.chantierId))],
                groupeId: (this.filterGroupe?.length === this.groupeList?.length || this.filterGroupe.length > 500) ? [] : this.filterGroupe?.map(x => x.id),
                sousGroupeId: (this.filterSousGroupe?.length === this.sousGroupeList?.length || this.filterSousGroupe.length > 500) ? [] : this.filterSousGroupe?.map(x => x.id),
                equipementId: this.equipementListFiltered?.length === this.filter.equipementList?.length ? [] : this.filter.equipementList.map(e => e.id),
                dateDebut: this.filterDocumentDateFrom !== '' ? this.filterDocumentDateFrom : undefined,
                dateFin: this.filterDocumentDateTo !== '' ? this.filterDocumentDateTo : undefined,
                isPreventif: this.isPreventif
            });
        },
        getListGroupBySite(){
            this.filtreGroupeLoading = true;
            const adressesSiteIds: number[] = this.filterSite.length !== this.siteListTotal.length ? this.filterSite.map(site => site.id) : [];
            SiteListService.getGroupeListBySites(adressesSiteIds).then(response => {
                this.groupeList = response.data.map(
                    ({ id, libelle, sousGroupes }:
                         { id: number; libelle: string, sousGroupes: [{ id: number; libelle: string}] }) => ({
                        id,
                        libelle,
                        sousGroupes
                    }));
                this.sousGroupeList = [];
                this.groupeList.forEach(groupe => {
                    if (groupe.sousGroupes != null) {
                        groupe.sousGroupes.forEach(ssGroupe => {
                            this.sousGroupeList.push(ssGroupe);
                        });
                    }
                });

                this.filter.groupeList = this.groupeList.length > 0 ? this.groupeList : [];
                this.filter.sousGroupeList = this.sousGroupeList.length > 0 ? this.sousGroupeList : [];

                this.$store.commit(`rapportState/${RAPPORT.SET_FILTER_GROUPE}`, this.filter.groupeList);
                this.filtreGroupeLoading = false;
            });
        },
        getEquipementList() {
            this.filtreEquipementLoading = true;
            const adressesSiteIds: number[] = this.filterSite.length !== this.siteListTotal.length ? this.filterSite.map(site => site.id) : [];

            EquipementService.getEquipementBySites(adressesSiteIds, '', '').then(async response => {
                this.equipementList = response.data.map(({id, libelle, phaseLibelle, groupeId, sousGroupeId}: {id: number; libelle: string, phaseLibelle: string, groupeId: number, sousGroupeId: number}) => ({
                    id,
                    libelle,
                    phaseLibelle,
                    groupeId,
                    sousGroupeId
                }));
                this.equipementListFiltered = this.equipementList;

                this.filter.equipementList = this.equipementListFiltered.length > 0 ? this.equipementListFiltered : [];
                this.$store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_EQUIPEMENT}`, this.filter.equipementList);
                this.filtreEquipementLoading = false;
            });
        },
        doFilter() {
            const equipementIds = this.filter.equipementList.map(e => e.id);

            this.$store.commit(`rapportState/${RAPPORT.RESET_ITEMS}`);

            this.$store.commit(`rapportState/${RAPPORT.SET_FILTER_GROUPE}`,
                this.filter.groupeList.length === this.groupeList.length ? [] : this.filter.groupeList);
            this.$store.commit(`rapportState/${RAPPORT.SET_FILTER_SOUS_GROUPE}`,
                this.filter.sousGroupeList.length === this.groupeList.length ? [] : this.filter.sousGroupeList);
            this.$store.commit(`rapportState/${RAPPORT.SET_FILTER_EQUIPEMENT}`,
                equipementIds.length === this.equipementList.length ? [] : equipementIds);
            this.$store.commit(`rapportState/${RAPPORT.SET_FILTER_DOCUMENT_DATE_FROM}`, this.filter.documentDateFrom);
            this.$store.commit(`rapportState/${RAPPORT.SET_FILTER_DOCUMENT_DATE_TO}`, this.filter.documentDateTo);

            this.getRapportData();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(`rapportState/${RAPPORT.SET_SORT}`, { sortData, sortStatus });
            this.$store.commit(`rapportState/${RAPPORT.RESET_ITEMS}`);
            this.getRapportData();
        },
        doSelect(item: {uniqueDocumentId: string}) {
            if(this.documentListToDownload?.find(element => element === item.uniqueDocumentId)){
                this.documentListToDownload = this.documentListToDownload.filter(element => element !== item.uniqueDocumentId);
            }else{
                this.documentListToDownload?.push(item.uniqueDocumentId);
            }
        },
        getDocuments(): void{
            const documentsToDownload: DocumentReglementaire[] =
                this.documentList.filter(document => this.documentListToDownload.find(element => document.uniqueDocumentId === element));
            this.rapportsInterventionToDownload = {
                interventionIds: documentsToDownload.map(doc => doc.interventionId),
                chantierIds: documentsToDownload.map(doc => doc.chantierId),
            };
            if (documentsToDownload.length === 1) {
                this.$store.dispatch(`rapportState/${RapportActions.GET_RAPPORT_PDF}`, this.rapportsInterventionToDownload);
                this.documentListToDownload = [];
            } else {
                this.showModalNomZip = true;
            }
            this.isDownloadAll = false;
        },
        downloadDocuments(nomZip: string): void {
            if(this.isDownloadAll) {
                this.filtreRapportIntervention.nomZip = nomZip;
                this.$store.dispatch(`rapportState/${RapportActions.GET_ALL_RAPPORT_PDF}`, this.filtreRapportIntervention);
                this.filtreRapportIntervention = {isPreventif: this.isPreventif};
            } else {
                this.rapportsInterventionToDownload.nomZip = nomZip;
                this.$store.dispatch(`rapportState/${RapportActions.GET_RAPPORT_PDF}`, this.rapportsInterventionToDownload);
                this.documentListToDownload = [];
            }
            this.showModalNomZip = false;
        },
        setFilterForDownloadAllDocuments(filterDoc: {documentDateFrom: string, documentDateTo: string}): void {
            this.filtreRapportIntervention = {
                dateDebut: filterDoc.documentDateFrom,
                dateFin: filterDoc.documentDateTo,
                chantierIds: this.siteList.length === this.filterSite.length ? [] : this.filterSite.map(site => site.chantierId),
                isPreventif: this.isPreventif,
                isForModal: true,
                size: 1,
            };
            this.$store.dispatch(`rapportState/${RapportActions.GET_RAPPORT}`, this.filtreRapportIntervention).then(() => {
                if(this.getNbRapports < 100) {
                    this.showModalNomZip = true;
                    this.showModalDownloadAll = false;
                }
            });
        },
        downloadAllDocuments(): void {
            this.isDownloadAll = true;
            if(this.count < 100) {
                this.filtreRapportIntervention = {
                    dateDebut: this.filter.documentDateFrom,
                    dateFin: this.filter.documentDateTo,
                    chantierIds: this.siteList.length === this.filterSite.length ? [] : this.filterSite.map(site => site.chantierId),
                    isPreventif: this.isPreventif
                };
                this.showModalNomZip = true;
            } else {
                this.showModalDownloadAll = true;
            }
        },
        setDocumentDateFrom(value: string): void {
            this.filter.documentDateFrom = value;
        },
        setDocumentDateTo(value: string): void {
            this.filter.documentDateTo = value;
        },
        addFilterGroupe(groupe: Groupe) {
            this.filter.groupeList.push(groupe);
            this.refreshSousGroupe();
            this.refreshEquipement();
        },
        addFilterSousGroupe(groupe: SousGroupe) {
            this.filter.sousGroupeList.push(groupe);
            this.refreshEquipementBySousGroupe();
        },
        removeFilterGroupe(groupe: Groupe) {
            this.filter.groupeList = this.filter.groupeList.filter(groupeitem => groupeitem !== groupe);
            this.refreshSousGroupe();
            this.refreshEquipement();
        },
        removeFilterSousGroupe(ssGroupe: SousGroupe) {
            this.filter.sousGroupeList = this.filter.sousGroupeList.filter(groupeitem => groupeitem !== ssGroupe);
            this.refreshEquipementBySousGroupe();
        },
        addFilterEquipement(equipement: EquipementFilter) {
            this.filter.equipementList.push(equipement);
        },
        removeFilterEquipement(equipement: EquipementFilter) {
            this.filter.equipementList = this.filter.equipementList.filter(equipementItem => equipementItem !== equipement);
        },
        groupeToggleSelectAll() {
            this.filter.groupeList = this.filter.groupeList.length === this.groupeList.length ? [] : this.groupeList;
            this.refreshSousGroupe();
            this.refreshEquipement();
        },
        sousGroupeToggleSelectAll() {
            this.filter.sousGroupeList = this.filter.sousGroupeList.length === this.sousGroupeList.length ? [] : this.sousGroupeList;
            this.refreshEquipement();
        },
        equipementToggleSelectAll() {
            this.filter.equipementList = this.filter.equipementList.length === this.equipementListFiltered.length ? [] : this.equipementListFiltered;
        },
        refreshSousGroupe() {
            this.sousGroupeList = [];
            this.filter.groupeList.forEach(groupe => {
                if (groupe.sousGroupes != null) {
                    groupe.sousGroupes.forEach((sousGroupe: SousGroupe) => this.sousGroupeList.push(sousGroupe));
                }
            });
            this.filter.sousGroupeList = this.sousGroupeList;
            this.sousGroupeList.sort();
        },
        refreshEquipement() {
            this.equipementListFiltered = [];
            this.filter.groupeList.forEach(groupe => {
                const equipements = this.equipementList.filter(equipement => equipement.groupeId === groupe.id);
                if (equipements != null) {
                    equipements.forEach(equipement => this.equipementListFiltered.push(equipement));
                }
            });
            this.filter.equipementList = this.equipementListFiltered;
        },
        refreshEquipementBySousGroupe() {
            this.equipementListFiltered = [];
            this.filter.sousGroupeList.forEach(sousGroupe => {
                const equipements = this.equipementList.filter(equipement => equipement.sousGroupeId === sousGroupe.id);
                if (equipements != null) {
                    equipements.forEach(equipement => this.equipementListFiltered.push(equipement));
                }
            });
            this.filter.equipementList = this.equipementListFiltered;
        },
        clearFilter() {
            this.filter.siteList = this.siteList ?? [];
            this.filter.equipementList = this.equipementList ?? [];
            this.filter.groupeList = this.groupeList ?? [];
            this.filter.sousGroupeList = this.sousGroupeList ?? [];
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.$store.commit(`rapportState/${RAPPORT.SET_PAGE}`, this.page + 1);
                this.getRapportData(this.page);
            }
        },
        validerSite(sites: Site[]) : void {
            this.displayModalChoixSite = false;
            store.commit(`rapportState/${RAPPORT.SET_FILTER_SITE}`, sites);
            store.commit(`rapportState/${RAPPORT.RESET_ITEMS}`);
            this.getRapportData();
            this.getListGroupBySite();
            this.getEquipementList();
        },
        modalChoixSiteCancel(): void {
            this.siteList = this.siteListTotal;
            this.displayModalChoixSite = false;
            if (this.tableData.length === 0 && !this.isLoading) {
                store.commit(`rapportState/${RAPPORT.SET_FILTER_SITE}`, this.siteList);
                this.getListGroupBySite();
                this.getEquipementList();
                this.getRapportData();
            }
        },
        selectionnerSite() : void {
            this.displayModalChoixSite = true;
        },
        cancelModalDownloadAll() {
            this.showModalDownloadAll = false;
        },
    },
    computed: {
        tableData(): DocumentReglementaire[] {
            return this.$store.getters['rapportState/getRapportItems'].map(
                ({uniqueDocumentId, interventionId, indiceFiche, adresseProjetDescription, groupeLibelle, sousGroupeLibelle, libelleEquipement, phaseLibelle, dateIntervention}: DocumentReglementaire) => {
                    return {
                        uniqueDocumentId: uniqueDocumentId,
                        interventionId: indiceFiche ? `${interventionId}-${indiceFiche}` : interventionId,
                        adresseProjetDescription: adresseProjetDescription,
                        groupeLibelle: groupeLibelle,
                        sousGroupeLibelle: sousGroupeLibelle,
                        libelleEquipement: {
                            libelle: libelleEquipement,
                            phase: phaseLibelle && phaseLibelle.trim().toLowerCase() !== 'maintenance' ? phaseLibelle : undefined,
                        },
                        dateIntervention: dateIntervention ? new Date(dateIntervention).toLocaleDateString() : '',
                    };
                }
            );
        },
        tableHeader(): TableHeader[] {
            return this.$store.getters['rapportState/getRapportTableHeader'];
        },
        documentList(): DocumentReglementaire[]{
            return this.$store.getters['rapportState/getRapportItems'];
        },
        isLoading() {
            return this.$store.getters['rapportState/getRapportIsLoading'];
        },
        page() {
            return this.$store.getters['rapportState/getRapportPage'];
        },
        numberOfPages() {
            return this.$store.getters['rapportState/getRapportNbOfPages'];
        },
        scroll() {
            return this.$store.getters['rapportState/getRapportScroll'];
        },
        filterSite(): any[] {
            return this.$store.getters['rapportState/getRapportFilterSite'];
        },
        filterGroupe(): any[] {
            return this.$store.getters['rapportState/getRapportFilterGroupe'];
        },
        filterSousGroupe(): any[] {
            return this.$store.getters['rapportState/getRapportFilterSousGroupe'];
        },
        filterEquipement(): any[] {
            return this.$store.getters['rapportState/getRapportFilterEquipement'];
        },
        filterDocumentDateFrom() {
            return this.$store.getters['rapportState/getRapportFilterDocumentDateFrom'];
        },
        filterDocumentDateTo() {
            return this.$store.getters['rapportState/getRapportFilterDocumentDateTo'];
        },
        isDownloadDisabled(): boolean {
            return this.tableData.length === 0 || this.documentListToDownload.length === 0;
        },
        count() {
            return this.$store.getters['rapportState/getRapportCount'];
        },
        isAtLeastOneReport() {
            return this.$store.getters['rapportState/getRapportCount'] >0 ;
        },
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters['rapportState/getRapportSort'];
        },
        iconeDownload() {
            return require('@/assets/img/pdf-icon.svg');
        },
        closeIcon() {
            return require('@/assets/img/close-white.svg');
        },
        isZipLoading() : boolean {
            return this.$store.getters['rapportState/getZipIsLoading'];
        },
        getNbRapports(): number {
            return this.$store.getters['rapportState/getNbRapports'];
        },
    }
});
</script>

<style lang="scss" scoped>
@import '@/theme/pages-recherche.scss';


@media screen and (max-width: 1631px) {
    .datatable-container {
        max-height: calc(100vh - 340px);
    }
}

@media screen and (max-width: 1536px) {
    .datatable-container {
        max-height: calc(100vh - 440px);
    }
}

@media screen and (max-width: 970px) {
    .datatable-container {
        max-height: calc(100vh - 518px);
    }
}

:deep(.containerBtn) {
    height: 20px;
}
:deep(.icon) {
    height: 20px;
}
.filter-container {
    display: flex;
    padding: 8px 16px;
    background: white;
    border-radius: 6px;
    margin-bottom: 8px;
}
.filter-site-container{
    flex-direction: column;
    width: 15%;
}
.filter {
    width: 100%;
}
.button-confirm {
    right: 16px;
}
.btn {
    width: 100%;
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin-top: 20px;
}
:deep(.filter) {
    margin-bottom: 0;
    padding: 0 16px;
}
.button-left {
    float: left;
    margin-right: 10px;
    height : 28px;
}
.button-right {
    float: right;
    height : 28px;

}
.radio-container {
  width: 22%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}
</style>
